import React, { ReactElement, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ProductsAnalyticsProvider } from 'b2b-apps/core/analytics/context/products/ProductsAnalyticsContext';
import { EVENT_SOURCE } from 'b2b-apps/core/analytics/types/types';
import { useBreadcrumbs } from 'b2b-apps/shop/breadcrumbs/hooks';
import { CrumbType } from 'b2b-apps/shop/breadcrumbs/redux/types';
import { PRINTERS_CATEGORY_URL } from 'b2b-apps/shop/common/components/InkFinderLink';
import { ensureArray } from 'b2b-apps/shop/common/utils/array/array';
import { isServer } from 'b2b-apps/shop/common/utils/env';
import { META_TAGS } from 'b2b-apps/shop/common/utils/seo/metaTags';
import { config } from 'b2b-apps/shop/config';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { useProductFullEffect } from 'b2b-apps/shop/product/hooks';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { getCategoryUrlParams, getCdnImageUrl, getProductFallbackImgUrl } from 'b2b-apps/shop/routes/utils/url';
import { EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (): ReactElement => {
    const t = useTranslate();
    const params = useParams<{ sku: string }>();
    const { pathname } = useLocation();
    const product = useProductFullEffect(params.sku || '');
    const { isLoaded, data: productData } = product;
    const urlCreator = useUrlCreator();
    const isProductDataSpecified = !!productData;
    const shopName = config.shopName;
    const title = productData?.description?.title || '';
    const description = (productData?.description?.bulletPoints || []).join(' - ');
    const imagesArray = ensureArray(productData?.cover?.sizes || []);
    const isImagePresent = imagesArray.length && imagesArray[0].link;
    const cdnImageUrl = isImagePresent && getCdnImageUrl(imagesArray[0].link);
    const imageUrl = cdnImageUrl || getProductFallbackImgUrl();

    const ogUrl = isServer ? `${config.url.root}${pathname}` : window.location.href;
    const getBreadcrumbs = (): CrumbType[] => {
      if (!isLoaded || !productData) {
        return [];
      }

      const breadcrumbs: CrumbType[] = productData.breadcrumbs.map(
        (category) => ({
          title: category.label,
          url: `${urlCreator(ROUTES.category)}${getCategoryUrlParams(
            category.url,
          )}`,
        }),
      );
      breadcrumbs.push({
        title: t('SKU') + ' ' + productData.sku,
      });
      return breadcrumbs;
    };

    useBreadcrumbs(
      getBreadcrumbs(),
    );

    const isInkFinderLinkDisplayed = useMemo(
      (): boolean => isLoaded && !!productData?.breadcrumbs
        ?.find(el => el.url === PRINTERS_CATEGORY_URL.de || el.url === PRINTERS_CATEGORY_URL.fr),
      [isLoaded, productData],
    );

    const metaTags = useMemo(() => {
      if (!isLoaded || !isProductDataSpecified) {
        return [];
      }

      return [
        {
          name: META_TAGS.description,
          content: description,
        },
        {
          name: META_TAGS.ogDescription,
          content: description,
        },
        {
          name: META_TAGS.ogImage,
          content: imageUrl,
        },
        {
          name: META_TAGS.ogTitle,
          content: title,
        },
        {
          name: META_TAGS.ogType,
          content: 'product',
        },
        {
          name: META_TAGS.ogUrl,
          content: ogUrl,
        },
        {
          name: META_TAGS.ogSitename,
          content: shopName,
        },
        {
          name: META_TAGS.title,
          content: title,
        },
        {
          name: META_TAGS.twitterCard,
          content: 'summary_large_image',
        },
        {
          name: META_TAGS.twitterDescription,
          content: description,
        },
        {
          name: META_TAGS.twitterImage,
          content: imageUrl,
        },
        {
          name: META_TAGS.twitterTitle,
          content: title,
        },
      ];
    }, [
      description,
      imageUrl,
      isLoaded,
      isProductDataSpecified,
      ogUrl,
      shopName,
      title,
    ]);

    const injectedProps: InjectedProps = {
      isInkFinderLinkDisplayed,
      product,
      metaTags,
    };

    return (
      <ProductsAnalyticsProvider eventSource={EVENT_SOURCE.pdp}>
        <Component {...injectedProps} />
      </ProductsAnalyticsProvider>
    );
  };

  return WithContainer;
};

export { withContainer };
